import { set } from '@ember/object';
import { getOwner } from '@ember/owner';
import { renderComponent } from '@ember/renderer';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { PRISM_SPACING_100 } from '@mylighthouse/prism-foundation';
import { task, timeout } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import {
  COLOR_PARITY_LOSS,
  COLOR_PARITY_MEET,
  COLOR_PARITY_WIN,
} from 'frontend-utils/utils/css-variable';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import { TOOLTIP_HOVER_TIMEOUT } from 'lumen/services/usage-analytics';
import { PARITY_FILTER } from 'lumen/utils/constants/pi-constant-values';

import styles from './chart-variance.module.css';

interface ParityChartsChartVarianceSignature {
  Args: {
    allVariances: unknown;
    hideHotelsMetric: unknown;
    showLoadingPlaceholderState: unknown;
  };
  Element: HTMLElement;
}

export default class ParityChartsChartVariance extends Component<ParityChartsChartVarianceSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Array to hold all possible buckets. This makes sure all graph indices are drawn even without data from API
  displayedVarianceBuckets = [
    -8, -7, -6, -5, -4, -3, -2, 1, 2, 3, 4, 5, 6, 7, 8,
  ];

  displayName = '';
  tagName = '';

  varianceMapping = {
    0: 'None',
    1: 'Meet',
    2: { valueA: 0, valueB: 5 },
    3: { valueA: 5, valueB: 10 },
    4: { valueA: 10, valueB: 15 },
    5: { valueA: 15, valueB: 20 },
    6: { valueA: 20, valueB: 25 },
    7: 25,
    8: 'Soldout',
  };

  trackHoverTask = task({ restartable: true }, async (varianceBucket) => {
    await timeout(TOOLTIP_HOVER_TIMEOUT);
    this.usageAnalytics.track('Price Difference Details Viewed', {
      eventTrigger: 'price-difference-chart',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      varianceBucket,
    });
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // @ts-expect-error: Incorrect type
    const win = [];
    // @ts-expect-error: Incorrect type
    const meet = [];
    // @ts-expect-error: Incorrect type
    const loss = [];

    if (this.args.allVariances) {
      this.displayedVarianceBuckets
        // Make buckets out of numbers array...
        .map((number) => {
          return {
            number,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
            variance: this.args.allVariances.filter(
              // @ts-expect-error: Incorrect type
              (variance) =>
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                parseInt(variance.segmentationValueId, 10) === number,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            )[0],
          };
        })
        // Generate points win/meet/loss for each of the generated buckets
        .forEach((bucket) => {
          if (bucket.number === 1) {
            // Plot meet on 0
            meet.push(this.generatePoint(bucket, 'meet'));
          } else if (bucket.number > 0) {
            // Positive variance
            win.push(this.generatePoint(bucket, 'win'));
          } else if (bucket.number < 0) {
            // Negative variance
            loss.push(this.generatePoint(bucket, 'loss'));
          }
        });

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      win.sort((a, b) => a.x - b.x);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      meet.sort((a, b) => a.x - b.x);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      loss.sort((a, b) => a.x - b.x);

      return [
        {
          metricId: PARITY_FILTER.LOSS,
          name: this.intl.t('parity_analytics.loss').toString(),
          // @ts-expect-error: Incorrect type
          data: loss,
          color: `var(${COLOR_PARITY_LOSS})`,
          visible: true,
          borderColor: 'var(--prism-color-border-inverse-emphasis)',
        },
        {
          metricId: PARITY_FILTER.MEET,
          name: this.intl.t('parity_analytics.meet').toString(),
          // @ts-expect-error: Incorrect type
          data: meet,
          color: `var(${COLOR_PARITY_MEET})`,
          visible: false,
          borderColor: 'var(--prism-color-border-inverse-emphasis)',
        },
        {
          metricId: PARITY_FILTER.WIN,
          name: this.intl.t('parity_analytics.win').toString(),
          // @ts-expect-error: Incorrect type
          data: win,
          color: `var(${COLOR_PARITY_WIN})`,
          visible: true,
          borderColor: 'var(--prism-color-border-inverse-emphasis)',
        },
      ];
    }

    return [];
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    set(this, 'chartOptions', {
      chart: {
        zoomType: null,
        type: 'column',
        title: '',
        height: null, // Grow chart to fill container
        spacingLeft: 0,
        spacingRight: 0,
        spacingBottom: 0,
      },

      title: {
        text: 'parity-variance-chart',
      },

      tooltip: {
        shared: false,
        hideDelay: 0.2,
        shape: 'square',
        padding: 0,
        borderWidth: 0,
        shadow: false,
        outside: true,
        distance: 20,

        formatter() {
          // @ts-expect-error: Incorrect type
          const { point } = this;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (!point.variance) {
            return null;
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const { metricId } = point.series.options;
          const element = document.createElement('div');
          renderComponent(ParityTooltipsTooltipKpi, {
            into: element,
            owner: getOwner(that),
            args: {
              data: {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                singleValue: point.variance.calculatedValues[metricId],
                singleValueTitle: that.intl.t(
                  'parity_analytics.percentage_total',
                ),
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                totalNbOfHotels: point.variance.value.subscriptions,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                totalNbOfShops: point.variance.value[metricId],
              },
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              displayName: that.xValue(point.x),
              hideShoppedHotels: that.args.hideHotelsMetric,
            },
          });

          return element.innerHTML;
        },
        useHTML: true,
      },

      xAxis: {
        tickInterval: 1,
        labels: {
          formatter() {
            // @ts-expect-error: Incorrect type
            return that.xValue(this.value);
          },
        },
      },

      yAxis: {
        // max: 100,
        title: {
          text: null,
        },
        labels: {
          formatter() {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            return that.intl.formatNumber(this.value, { style: 'percent' });
          },
        },
      },

      legend: {
        padding: PRISM_SPACING_100,
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        symbolWidth: 7,
        symbolHeight: 7,
        itemStyle: {
          fontSize: '12px',
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },

      plotOptions: {
        series: {
          pointPadding: 0.03,
          groupPadding: 0.03,
          shadow: false,
          pointWidth: null,
          stacking: 'normal',
        },

        column: {
          point: {
            events: {
              mouseOver() {
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                that.trackHoverTask.perform(that.xValue(this.x));
              },
              mouseOut() {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                that.trackHoverTask.cancelAll();
              },
            },
          },
        },
      },
    });
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  generatePoint(bucket, key) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    let xValue = Math.abs(bucket.number) - 1;
    let yValue = 0;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (bucket.variance) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      yValue = bucket.variance.calculatedValues[key];
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (bucket.number < 0) {
      xValue = -xValue;
    }

    return {
      x: xValue,
      y: yValue,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      variance: bucket.variance,
    };
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  xValue(point) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const bucket = Math.abs(point) + 1;
    const varianceTranslationKey = tKey('parity_analytics.variance.until');
    // let priceDifferenceTextPrepend = '';

    // If long format is requested explicitly, set translations to long and prepend with 'Price discrepancy: '
    // if (isLongFormat) {
    //   translationKey = tKey('parity_analytics.variance.until.long');
    //   priceDifferenceTextPrepend = `${get(this, 'intl').t(tKey('parity_analytics.variance.discrepancy`)}: ');
    // }

    // 8 is the bucket for Soldout
    if (bucket === 8) {
      return this.intl.t('parity_analytics.variance.soldout').toString();
    }

    // 7 is the bucket for anything above - atm - 25
    if (bucket === 7) {
      const value = this.varianceMapping[bucket];

      if (point > 0) {
        return `+ ${value}%`;
      }

      return `- ${value}%`;
    }

    if (point > 0) {
      return `${this.intl.t(varianceTranslationKey, {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        a: `${this.varianceMapping[bucket].valueA}%`,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        b: `${this.varianceMapping[bucket].valueB}%`,
      })}`;
    }
    if (point < 0) {
      return `${this.intl.t(varianceTranslationKey, {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        a: `${-this.varianceMapping[bucket].valueB}%`,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        b: `${-this.varianceMapping[bucket].valueA}%`,
      })}`;
    }

    return `${this.intl.t('parity_analytics.meet')}`;
  }

  <template>
    <PrismCard ...attributes>
      <:header as |Header|>
        <div class="flex items-center">
          {{#if @showLoadingPlaceholderState}}
            <p class="loading-pulse--col-3"></p>
          {{else}}
            <Header.title>
              {{t "parity_analytics.variance_bucket"}}
            </Header.title>
          {{/if}}
        </div>
      </:header>
      <:content>
        <div class={{styles.breakdown-content}}>
          <GeneralChartsOtaChart
            {{! @glint-expect-error: Incorrect type }}
            @chartOptions={{this.chartOptions}}
            @model={{this.chartData}}
            class="h-full"
          />
        </div>
      </:content>
    </PrismCard>
  </template>
}
